import { useState, useEffect, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'

interface FileOp {
  kind: 'file' | 'diff'
  action: 'write' | 'delete'
  relPath: string
  storedPath: string
  content: string
  diff?: string
}

function parsePulseFileOp(code: string): FileOp | null {
  const lines = code.split(/\r?\n/)
  let action: 'write' | 'delete' = 'write'
  let relPath: string | null = null
  let lastHeaderIndex = -1

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) { if (lastHeaderIndex !== -1) continue; continue }
    const fileMatch = line.match(/^\/\/\s*FILE\s*:\s*(.+)\s*$/i)
    const actionMatch = line.match(/^\/\/\s*ACTION\s*:\s*(delete|remove|write|update|create)\s*$/i)
    if (fileMatch) {
      const raw = fileMatch[1].trim().replace(/^[.]{0,2}\//, '').replace(/^[/\\]+/, '').replace(/\\/g, '/')
      if (raw) { relPath = raw; lastHeaderIndex = i }
      continue
    }
    if (actionMatch) {
      const a = actionMatch[1].toLowerCase()
      action = (a === 'delete' || a === 'remove') ? 'delete' : 'write'
      lastHeaderIndex = i
      continue
    }
    if (lastHeaderIndex !== -1) break
    break
  }

  if (!relPath) return null
  const body = lastHeaderIndex >= 0 ? lines.slice(lastHeaderIndex + 1).join('\n') : ''
  return {
    kind: 'file',
    action,
    relPath,
    storedPath: '/' + relPath,
    content: action === 'delete' ? '' : body.trimEnd(),
  }
}

function parseDiffBlock(code: string): FileOp | null {
  const trimmed = code.trim()
  const firstLine = trimmed.split('\n')[0] ?? ''
  const fileMatch = firstLine.match(/^---\s+(.+)/)
  if (!fileMatch) return null
  const relPath = fileMatch[1].trim().replace(/^[.]{0,2}\//, '').replace(/^[/\\]+/, '').replace(/\\/g, '/')
  if (!relPath) return null
  return {
    kind: 'diff',
    action: 'write',
    relPath,
    storedPath: '/' + relPath,
    content: '',
    diff: trimmed,
  }
}

function extractOps(text: string): FileOp[] {
  const blocks: string[] = []
  const regex = /```(\w*)\n?([\s\S]*?)```/g
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    blocks.push(match[2].trim())
  }

  const ops: FileOp[] = []
  for (const block of blocks) {
    if (block.startsWith('---')) {
      const d = parseDiffBlock(block)
      if (d) ops.push(d)
      continue
    }
    const f = parsePulseFileOp(block)
    if (f && f.relPath !== '__terminal__') ops.push(f)
  }
  return ops
}

function DiffPreview({ oldText, newText }: { oldText: string; newText: string }) {
  const oldLines = oldText.split('\n')
  const newLines = newText.split('\n')
  const maxLen = Math.max(oldLines.length, newLines.length)
  const lines: { type: 'same' | 'add' | 'remove'; text: string }[] = []
  for (let i = 0; i < maxLen; i++) {
    const o = oldLines[i]
    const n = newLines[i]
    if (o === n) { if (o !== undefined) lines.push({ type: 'same', text: o }) }
    else {
      if (o !== undefined) lines.push({ type: 'remove', text: o })
      if (n !== undefined) lines.push({ type: 'add', text: n })
    }
  }
  return (
    <pre style={{ fontSize: 10, fontFamily: 'var(--font-mono)', margin: 0, whiteSpace: 'pre-wrap' }}>
      {lines.map((l, i) => (
        <div key={i} style={{
          background: l.type === 'add' ? 'rgba(95,212,164,0.12)' : l.type === 'remove' ? 'rgba(240,112,112,0.12)' : 'none',
          color: l.type === 'add' ? 'var(--accent-2)' : l.type === 'remove' ? 'var(--red)' : 'var(--text-1)',
          padding: '0 4px',
        }}>
          <span style={{ display: 'inline-block', width: 14, color: 'var(--text-2)', userSelect: 'none' }}>
            {l.type === 'add' ? '+' : l.type === 'remove' ? '-' : ' '}
          </span>
          {l.text}
        </div>
      ))}
    </pre>
  )
}

function DiffBlockPreview({ diff }: { diff: string }) {
  return (
    <pre style={{ fontSize: 10, fontFamily: 'var(--font-mono)', margin: 0, whiteSpace: 'pre-wrap' }}>
      {diff.split('\n').map((line, i) => {
        const type = line.startsWith('+') ? 'add' : line.startsWith('-') ? 'remove' : line.startsWith('@@') ? 'diff' : 'same'
        return (
          <div key={i} style={{
            background: type === 'add' ? 'rgba(95,212,164,0.12)' : type === 'remove' ? 'rgba(240,112,112,0.12)' : type === 'diff' ? 'rgba(124,110,245,0.08)' : 'none',
            color: type === 'add' ? 'var(--accent-2)' : type === 'remove' ? 'var(--red)' : type === 'diff' ? 'var(--accent)' : 'var(--text-1)',
            padding: '0 4px',
          }}>{line}</div>
        )
      })}
    </pre>
  )
}

export function FileApprovalPopup() {
  const [queue, setQueue] = useState<FileOp[]>([])
  const [currentIdx, setCurrentIdx] = useState(0)
  const [applied, setApplied] = useState<string[]>([])
  const [skipped, setSkipped] = useState<string[]>([])
  const [oldContent, setOldContent] = useState<string | null>(null)

  const currentOp = queue[currentIdx]

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail?.content) return
      const fileOps = extractOps(detail.content)
      if (fileOps.length > 0) {
        setQueue(fileOps)
        setCurrentIdx(0)
        setApplied([])
        setSkipped([])
      }
    }
    window.addEventListener('pulse:approve-files', handler)
    return () => window.removeEventListener('pulse:approve-files', handler)
  }, [])

  useEffect(() => {
    if (!currentOp || currentOp.kind === 'diff') {
      setOldContent(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(currentOp.relPath)}`))
        if (!res.ok) { if (!cancelled) setOldContent(''); return }
        const data = await res.json()
        if (!cancelled) setOldContent(data.content ?? '')
      } catch {
        if (!cancelled) setOldContent('')
      }
    })()
    return () => { cancelled = true }
  }, [currentOp])

  const advance = useCallback(() => {
    if (currentIdx < queue.length - 1) {
      setCurrentIdx((prev) => prev + 1)
    } else {
      setTimeout(() => setQueue([]), 2000)
    }
  }, [currentIdx, queue.length])

  const handleApply = useCallback(async () => {
    if (!currentOp) return

    try {
      if (currentOp.kind === 'diff' && currentOp.diff) {
        const res = await authFetch(apiUrl('/api/agent-stream/apply-patch'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filePath: currentOp.relPath, diff: currentOp.diff }),
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          alert(`⚠️ Patch failed: ${err.error || res.status}`)
          setSkipped((prev) => [...prev, currentOp.relPath])
          advance()
          return
        }
        const data = await res.json()
        const readRes = await authFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(data.path || currentOp.relPath)}`))
        if (readRes.ok) {
          const { content } = await readRes.json()
          const existingTab = useAppStore.getState().openTabs.find((t) => t.path === currentOp.storedPath)
          if (existingTab) {
            useAppStore.getState().snapshotFile(currentOp.storedPath, existingTab.content)
            useAppStore.getState().markDirty(existingTab.id, content)
          } else {
            useAppStore.getState().openTab({
              id: currentOp.storedPath,
              path: currentOp.storedPath,
              name: currentOp.storedPath.split('/').at(-1) ?? currentOp.storedPath,
              language: 'text',
              content,
            })
          }
        }
      } else {
        const writeRes = await authFetch(apiUrl('/api/files/write'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: currentOp.relPath, content: currentOp.content }),
        })

        if (!writeRes.ok) {
          const err = await writeRes.json().catch(() => ({}))
          if (writeRes.status === 409) {
            alert(`⚠️ BLOCKED: ${err.message || 'This would delete existing code.'}\n\nUse AGENT mode for patch diffs instead.`)
          }
          setSkipped((prev) => [...prev, currentOp.relPath])
          advance()
          return
        }

        const existingTab = useAppStore.getState().openTabs.find((t) => t.path === currentOp.storedPath)
        if (existingTab) {
          useAppStore.getState().snapshotFile(currentOp.storedPath, existingTab.content)
          useAppStore.getState().markDirty(existingTab.id, currentOp.content)
        } else {
          useAppStore.getState().openTab({
            id: currentOp.storedPath,
            path: currentOp.storedPath,
            name: currentOp.storedPath.split('/').at(-1) ?? currentOp.storedPath,
            language: 'text',
            content: currentOp.content,
          })
        }
      }

      setApplied((prev) => [...prev, currentOp.relPath])
      // Trigger editor diff highlights - use snapshot (pre-edit content) for comparison
      const snapshot = useAppStore.getState().getSnapshot(currentOp.storedPath)
      const compareOld = snapshot || oldContent
      if (compareOld && compareOld.trim() && currentOp.content !== compareOld) {
        useAppStore.getState().setAgentFilePatch(currentOp.storedPath, compareOld, currentOp.content)
        window.dispatchEvent(new CustomEvent('pulse:agent-file-patched', { detail: { path: currentOp.storedPath, content: currentOp.content } }))
      }
      window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      advance()
    } catch {
      alert('Failed to apply change. Skipping.')
      setSkipped((prev) => [...prev, currentOp.relPath])
      advance()
    }
  }, [currentOp, advance])

  const handleSkip = useCallback(() => {
    if (!currentOp) return
    setSkipped((prev) => [...prev, currentOp.relPath])
    advance()
  }, [currentOp, advance])

  const handleSkipAll = useCallback(() => {
    const remaining = queue.slice(currentIdx).map((op) => op.relPath)
    setSkipped((prev) => [...prev, ...remaining])
    setTimeout(() => setQueue([]), 1500)
  }, [queue, currentIdx])

  if (queue.length === 0) return null

  if (currentIdx >= queue.length) {
    return (
      <div style={overlayStyle} onClick={() => setQueue([])}>
        <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-0)', marginBottom: 12 }}>
            ✓ Done — {applied.length} applied, {skipped.length} skipped
          </div>
          <button onClick={() => setQueue([])} style={applyBtnStyle}>Close</button>
        </div>
      </div>
    )
  }

  return (
    <div style={overlayStyle}>
      <div style={popupStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
            File {currentIdx + 1} of {queue.length}
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-2)' }}>
            ✓ {applied.length} applied · ✗ {skipped.length} skipped
          </span>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>
          {currentOp.action === 'delete' ? '🗑 DELETE: ' : currentOp.kind === 'diff' ? '📝 PATCH: ' : '📄 '}{currentOp.relPath}
        </div>

        <div style={{ maxHeight: 300, overflow: 'auto', background: 'var(--bg-0)', borderRadius: 6, padding: 8, border: '1px solid var(--border)', marginBottom: 12 }}>
          {currentOp.kind === 'diff' && currentOp.diff ? (
            <DiffBlockPreview diff={currentOp.diff} />
          ) : oldContent !== null && oldContent !== '' ? (
            <DiffPreview oldText={oldContent} newText={currentOp.content} />
          ) : (
            <pre style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', margin: 0, whiteSpace: 'pre-wrap' }}>
              {currentOp.content.substring(0, 2000)}{currentOp.content.length > 2000 ? '\n... (truncated)' : ''}
            </pre>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleApply} style={applyBtnStyle}>✓ Apply</button>
          <button onClick={handleSkip} style={skipBtnStyle}>✗ Skip</button>
          <button onClick={handleSkipAll} style={skipAllBtnStyle}>Skip All</button>
        </div>
      </div>
    </div>
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', zIndex: 9999,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
}

const popupStyle: React.CSSProperties = {
  background: 'var(--bg-1)', borderRadius: 12, padding: 20,
  border: '1px solid var(--border-2)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  width: '90%', maxWidth: 600,
}

const applyBtnStyle: React.CSSProperties = {
  flex: 1, padding: '8px 16px', borderRadius: 6, border: 'none',
  background: 'var(--accent-2)', color: '#000', fontWeight: 700,
  fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-mono)',
}

const skipBtnStyle: React.CSSProperties = {
  flex: 1, padding: '8px 16px', borderRadius: 6,
  border: '1px solid var(--border)', background: 'none',
  color: 'var(--text-1)', fontWeight: 600, fontSize: 12,
  cursor: 'pointer', fontFamily: 'var(--font-mono)',
}

const skipAllBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 6,
  border: '1px solid rgba(240,112,112,0.3)', background: 'none',
  color: 'var(--red)', fontWeight: 600, fontSize: 12,
  cursor: 'pointer', fontFamily: 'var(--font-mono)',
}
